/**
 * Release / environment metadata for observability.
 * Read-only helpers — no behaviour change.
 */
import { APP_VERSION, BUILD_STAMP, IOS_BUILD, IOS_BUILD_STAMP } from "@/lib/appVersion";

function safeCapacitor(): { native: boolean; platform: string } {
  try {
    // Avoid a hard dependency at module scope for web-only bundles.
    const cap = (window as any)?.Capacitor;
    if (cap?.isNativePlatform?.()) {
      return { native: true, platform: cap.getPlatform?.() || "native" };
    }
    return { native: false, platform: cap?.getPlatform?.() || "web" };
  } catch {
    return { native: false, platform: "web" };
  }
}

export interface ReleaseInfo {
  environment: string;
  release: string;
  /** Literal build stamp ("TM_BUILD:APP_VERSION=x.y") — proves which bundle is running. */
  buildStamp: string;
  /** Literal iOS build stamp ("TM_BUILD:IOS_BUILD=n") — release-integrity marker. */
  iosBuildStamp: string;
  appVersion: string;
  buildNumber: string;
  gitCommit: string;
  platform: string;
  isNative: boolean;
  buildTime: string;
}

export function getReleaseInfo(): ReleaseInfo {
  const { native, platform } = safeCapacitor();
  const env =
    (import.meta.env.VITE_APP_ENV as string | undefined) ||
    (import.meta.env.DEV ? "development" : "production");
  // iOS reports the real native build number embedded in this bundle so
  // telemetry build_number matches CFBundleVersion of the archive.
  const buildNumber =
    (import.meta.env.VITE_BUILD_NUMBER as string | undefined) ||
    (native && platform === "ios" ? IOS_BUILD : APP_VERSION.replace(".", ""));
  // Embedded at build time by vite (`__GIT_COMMIT__`) so a production bundle
  // — including the native embedded bundle — always reports its own commit.
  const embeddedCommit = (() => {
    try {
      return typeof __GIT_COMMIT__ === "string" ? __GIT_COMMIT__ : "";
    } catch {
      return "";
    }
  })();
  const gitCommit =
    (import.meta.env.VITE_GIT_COMMIT as string | undefined) || embeddedCommit || "unknown";

  const buildTime = (() => {
    try {
      return typeof __BUILD_TIME__ === "string" ? __BUILD_TIME__ : "";
    } catch {
      return "";
    }
  })();

  return {
    environment: env,
    release: `transportmate@${APP_VERSION}`,
    buildStamp: BUILD_STAMP,
    iosBuildStamp: IOS_BUILD_STAMP,
    appVersion: APP_VERSION,
    buildNumber,
    gitCommit,
    platform: native ? `native-${platform}` : "web",
    isNative: native,
    buildTime,
  };
}

/** Coarse device descriptor — never includes identifiers. */
export function getDeviceDescriptor(): string {
  try {
    const ua = navigator.userAgent || "";
    const width = window.innerWidth;
    const mobile = /Android|iPhone|iPad|iPod/i.test(ua);
    const browser = /CriOS|Chrome/i.test(ua)
      ? "Chrome"
      : /Firefox/i.test(ua)
      ? "Firefox"
      : /Safari/i.test(ua)
      ? "Safari"
      : /Edg/i.test(ua)
      ? "Edge"
      : "Other";
    const os = /Android/i.test(ua)
      ? "Android"
      : /iPhone|iPad|iPod/i.test(ua)
      ? "iOS"
      : /Mac OS X/i.test(ua)
      ? "macOS"
      : /Windows/i.test(ua)
      ? "Windows"
      : "Other";
    return `${os}/${browser}/${mobile ? "mobile" : "desktop"}/${width}w`;
  } catch {
    return "unknown";
  }
}

// ── Phase 14: runtime diagnostics context ────────────────────────
export interface RuntimeContext {
  os_version: string;
  device_model: string;
  network_state: string;
  capacitor_version: string;
  viewport: string;
}

/**
 * Coarse, non-identifying runtime context attached to telemetry.
 * Never includes identifiers, IMEI, advertising ids or user data.
 */
export function getRuntimeContext(): RuntimeContext {
  const ua = (() => {
    try {
      return navigator.userAgent || "";
    } catch {
      return "";
    }
  })();

  const osVersion = (() => {
    const ios = ua.match(/OS (\d+[_.]\d+)/);
    if (ios) return `iOS ${ios[1].replace("_", ".")}`;
    const android = ua.match(/Android (\d+(\.\d+)?)/);
    if (android) return `Android ${android[1]}`;
    const mac = ua.match(/Mac OS X (\d+[_.]\d+)/);
    if (mac) return `macOS ${mac[1].replace("_", ".")}`;
    const win = ua.match(/Windows NT (\d+\.\d+)/);
    if (win) return `Windows NT ${win[1]}`;
    return "unknown";
  })();

  const deviceModel = (() => {
    if (/iPad/i.test(ua)) return "iPad";
    if (/iPhone/i.test(ua)) return "iPhone";
    const android = ua.match(/Android [^;]+; ([^);]+)\)/);
    if (android) return android[1].trim().slice(0, 40);
    return /Mobile/i.test(ua) ? "Mobile" : "Desktop";
  })();

  const networkState = (() => {
    try {
      const conn = (navigator as unknown as { connection?: { effectiveType?: string } }).connection;
      const online = navigator.onLine ? "online" : "offline";
      return conn?.effectiveType ? `${online}/${conn.effectiveType}` : online;
    } catch {
      return "unknown";
    }
  })();

  const capacitorVersion = (() => {
    try {
      const cap = (window as any)?.Capacitor;
      return cap?.version || cap?.Plugins?.CapacitorHttp ? String(cap?.version || "native") : "n/a";
    } catch {
      return "n/a";
    }
  })();

  const viewport = (() => {
    try {
      return `${window.innerWidth}x${window.innerHeight}`;
    } catch {
      return "unknown";
    }
  })();

  return {
    os_version: osVersion,
    device_model: deviceModel,
    network_state: networkState,
    capacitor_version: capacitorVersion,
    viewport,
  };
}
